/* <sticky-atc> — sticky add-to-cart bar. Markup contract:
   - carries [data-section-id]; binds to the [data-main-atc] form whose
     product root has the SAME section id (falling back to one inside its own
     .shopify-section wrapper — never a document-wide first match); toggles
     its own [visible] attribute once that form has scrolled up out of view
   - [data-sticky-submit]  button; submits the section's form via
     requestSubmit() so cart.js's <product-form> intercepts it — one ATC code
     path. Ignored and kept disabled while the form is busy (data-busy,
     cart.js's single-flight guard)
   - optional [data-price] / [data-compare-price] mirrors, kept in sync via
     the bus 'variant:changed' detail.unitPrice/comparePrice (plan-adjusted);
     events from other sections are ignored via detail.sectionId
   - optional label span [data-atc-text] inside [data-sticky-submit] with
     data-add/data-sold-out/data-unavailable strings, swapped on
     'variant:changed' (same contract as product-form.js) */

import { bus, formatMoney } from './critical.js';

class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId || '';
    this.mainForm =
      [...document.querySelectorAll('[data-main-atc]')].find(
        (el) => el.closest('[data-section-id]')?.dataset.sectionId === this.sectionId
      ) || this.closest('.shopify-section')?.querySelector('[data-main-atc]');
    if (!this.mainForm) return;
    this.form = this.mainForm.matches('form')
      ? this.mainForm
      : this.mainForm.querySelector('form[action*="/cart/add"]');

    // #331 (Eli 2026-09-13): the bar shows once the main form has scrolled past the top of the viewport
    // and hides again as soon as the footer comes into view, live's rule (its handler compared scrollY to
    // the footer's offsetTop). Two observers, one shared sync, so neither can flip the other's state.
    this.formPassed = false;
    this.footerInView = false;
    const sync = () => this.toggleAttribute('visible', this.formPassed && !this.footerInView);
    this.io = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.formPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      sync();
    });
    this.io.observe(this.mainForm);
    // the footer group is the stable hook (the id hash differs per theme); never a bare "footer" — a review
    // widget prints its own <footer> inside <main>.
    const footer = document.querySelector('.shopify-section-group-footer-group');
    if (footer) {
      this.footerIo = new IntersectionObserver(([entry]) => {
        if (!entry) return;
        this.footerInView = entry.isIntersecting;
        sync();
      });
      this.footerIo.observe(footer);
    }

    this.submit = this.querySelector('[data-sticky-submit]');
    if (this.submit) {
      this.available = !this.submit.hasAttribute('disabled');
      this.submit.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.form;
        if (!form || form.dataset.busy) return;
        if (form.requestSubmit) form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });
      if (this.form) {
        /* Mirror the form's single-flight state onto the sticky button. */
        this.busyWatch = new MutationObserver(() => this.syncDisabled());
        this.busyWatch.observe(this.form, { attributes: true, attributeFilter: ['data-busy'] });
      }
    }

    this.unsubscribe = bus.on('variant:changed', (event) => {
      const detail = event.detail;
      if (!detail || detail.sectionId !== this.sectionId) return;
      const { variant, unitPrice, comparePrice, available } = detail;
      for (const el of this.querySelectorAll('[data-price]')) {
        el.textContent = variant ? formatMoney(unitPrice) : '';
      }
      const showCompare = !!variant && comparePrice > unitPrice;
      for (const el of this.querySelectorAll('[data-compare-price]')) {
        el.textContent = showCompare ? formatMoney(comparePrice) : '';
        el.toggleAttribute('hidden', !showCompare);
      }
      if (this.submit) {
        this.available = !!variant && !!available;
        this.syncDisabled();
        const label = this.submit.querySelector('[data-atc-text]');
        if (label) {
          const text = !variant
            ? label.dataset.unavailable
            : available
              ? label.dataset.add
              : label.dataset.soldOut;
          if (text) label.textContent = text;
        }
      }
    });
  }

  syncDisabled() {
    this.submit.toggleAttribute('disabled', !this.available || !!this.form?.dataset.busy);
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.footerIo?.disconnect();
    this.busyWatch?.disconnect();
    if (this.unsubscribe) this.unsubscribe();
  }
}

if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);
